import React from 'react';
import i18n from "I18n";

import { ReactHookFormMarkDown } from "components/base/CustomMarkDownArea";
import ReactHookFormTextInput from "components/reactHookFormComponents/ReactHookFormTextInput";
import { CampaignType, COUPON_SET_MEDIUM, CustomerSelectionBehavior, LanguageConfig } from 'config/CustomEnums';
import PhotoUploaderWithSwitch from 'components/campaign/campaignCreation/PhotoUploaderWithSwitch';
import ReactHookFormMarkDownArea from 'components/reactHookFormComponents/ReactHookFormMarkDownArea';
import CouponDateTimeSelector from 'components/coupon/couponCreation/CouponDateTimeSelector';
import StoreSelector from 'components/campaignStreamline/StoreSelector';
import ReactHookFormTitleWithRadio from 'components/reactHookFormComponents/ReactHookFormTitleWithRadio';
import CouponSettingSection from 'components/campaignStreamline/CouponSettingSection';
import { CampaignStreamlineGenerateCouponSection } from 'components/campaignStreamline/GenerateCouponSection';
import ReactHookFormAdditionalDataSection from 'components/reactHookFormComponents/ReactHookFormAdditionalDataSection';
import ReactHookFormSwitch from 'components/reactHookFormComponents/ReactHookFormSwitch';
import ReactHookFormMulitSelectDropdown from 'components/reactHookFormComponents/ReactHookFormMulitSelectDropdown';
import {
  BASE_COUPON_PAGE_ANCHOR_KEY,
  CAMPAIGN_COUPON_PAGE_ANCHOR_KEY
} from 'config/PageAnchorConfig/CampaignAnchorConfig';

export const getCouponSetMedium = (stores=[]) => {
  if (!stores?.length > 0) {
    return null
  }
  const includeOnlineStore = stores.some(store => store?.isOnlineStore)
  const includeOfflineStore = stores.some(store => !store?.isOnlineStore)
  if (includeOnlineStore && includeOfflineStore) {
    return COUPON_SET_MEDIUM.ONLIEN_OR_OFFLINE
  }
  if (includeOnlineStore) {
    return COUPON_SET_MEDIUM.ONLINE
  }
  if (includeOfflineStore) {
    return COUPON_SET_MEDIUM.OFFLINE
  }
  return null
}

export const couponSetFieldPrefix = "campaignCouponTemplate."

export const isCouponSetContentSameAsCampaign = (formContext) => {
  const { getValues } = formContext;
  const campaignType = getValues("campaignType");
  const couponSetContentSameAsCampaign = getValues(`${couponSetFieldPrefix}couponSetContentSameAsCampaign`);
  const haveCreatedCoupon = getValues(`haveCreatedCoupon`);
  return campaignType === CampaignType.couponsCampaign && !haveCreatedCoupon && couponSetContentSameAsCampaign
}


export const syncSourceTranslationToTarget = ({
  getValues,
  setValue,
  sourceTranslationData,
  sourceTranslationsFieldName,
  targetTranslationsFieldName,
  syncFieldsMap ={} // {sourceField: targetField, ...}
}) => {
  const sourceTranslationsValue = sourceTranslationData || getValues(sourceTranslationsFieldName);
  const targetTranslations = getValues(targetTranslationsFieldName);

  const sourceLanguages = Object.keys(sourceTranslationsValue) || [];

  let newTargetTranslations = sourceLanguages?.reduce((res, language) => {
    const resLanguageData = {
      ...targetTranslations?.[language]
    }
    Object.entries(syncFieldsMap).forEach(([sourceField, targetField]) => {
      resLanguageData[targetField] = sourceTranslationsValue?.[language]?.[sourceField]
    })
    res[language] = resLanguageData
    return res
  }, {})

  newTargetTranslations = { ...targetTranslations, ...newTargetTranslations, }
  setValue(targetTranslationsFieldName, newTargetTranslations, { shouldDirty: true })
}
export const syncCampaignContentToCouponTemplate = (formContext) => {
  if (!isCouponSetContentSameAsCampaign(formContext)) {
    return
  }
  const { getValues, setValue } = formContext;

  syncSourceTranslationToTarget({
    getValues,
    setValue,
    sourceTranslationsFieldName:"translations",
    targetTranslationsFieldName: `${couponSetFieldPrefix}translations`,
    syncFieldsMap: {
      generalName: "name",
      shortDescription: "shortDescription",
      detailSectionTitle: "detailSectionTitle",
      detailSectionContent: "detailSectionContent",
      instructionSectionTitle: "instructionSectionTitle",
      instructionSectionContent: "instructionSectionContent",
      coverPhoto: "coverPhoto",
      detailPhotos: "detailPhotos",
    }
  })
}

export const getCouponPageConfig = ({
  fieldPrefix = "",
  isCampaign = false,
}) => ({
  extraValidate: ({ getValues, setError, clearErrors }) => true,
  getDisplayComponentKeys: ({ formContext: { watch } }) => {
    const campaignType = watch("campaignType")
    let displayNormalSectionComponents = [
    ];
    if (isCampaign && campaignType === CampaignType.couponsCampaign) {
      displayNormalSectionComponents.push(
        CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.sameAsCampaignSection.sectionContent.same_as_campaign_key,
      )
    }
    let displayLanguageSectionComponents = [
      BASE_COUPON_PAGE_ANCHOR_KEY.couponGeneral.sectionContent.general_name_key,
      BASE_COUPON_PAGE_ANCHOR_KEY.couponGeneral.sectionContent.short_description_optional_key,
      BASE_COUPON_PAGE_ANCHOR_KEY.couponKeyVisual.sectionContent.cover_photo_upload_key,
      BASE_COUPON_PAGE_ANCHOR_KEY.couponKeyVisual.sectionContent.other_photo_upload_key,
      BASE_COUPON_PAGE_ANCHOR_KEY.couponInstruction.sectionContent.instruction_section_key,
      BASE_COUPON_PAGE_ANCHOR_KEY.couponDetail.sectionContent.detail_section_key,
      BASE_COUPON_PAGE_ANCHOR_KEY.validPeriod.sectionContent.valid_period_type_key,
      BASE_COUPON_PAGE_ANCHOR_KEY.applicableStores.sectionContent.store_selector_key,
      BASE_COUPON_PAGE_ANCHOR_KEY.applicableStores.sectionContent.coupon_required_select_store_key,
      BASE_COUPON_PAGE_ANCHOR_KEY.applicableStores.sectionContent.related_brand_optional_key,
      BASE_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionContent.coupon_properties_section_key,
      BASE_COUPON_PAGE_ANCHOR_KEY.generateCoupons.sectionContent.generate_coupons_key,
      BASE_COUPON_PAGE_ANCHOR_KEY.additionalData.sectionContent.additional_data_key,
    ];
    const couponRequiredStore = watch(`${fieldPrefix}shouldBeUsedInStores`)
      
    if (couponRequiredStore) {
      displayLanguageSectionComponents.push(
        BASE_COUPON_PAGE_ANCHOR_KEY.applicableStores.sectionContent.time_to_select_store_key,
      )
    }

    return {
      displayNormalSectionComponents,
      displayLanguageSectionComponents,
    }
  },
  normalSections: [
    {
      sectionKey: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.sameAsCampaignSection.sectionKey,
      sectionTitle: '',
      sectionContent: [
        {
          key: CAMPAIGN_COUPON_PAGE_ANCHOR_KEY.sameAsCampaignSection.sectionContent.same_as_campaign_key,
          component: ReactHookFormSwitch,
          componentProps: {
            title: "COUPON SET CONTENT IS SAME AS CAMPAIGN",
            customTitleClass: "campaign-streamline-display-in-frontend",
            valueFieldName: `${couponSetFieldPrefix}couponSetContentSameAsCampaign`,
            defaultTrue: true,
            extraActionOnChange: ({ formContext, value })=> {
              if (value) {
                syncCampaignContentToCouponTemplate(formContext)
              }
            }
          },
        }
      ]
    },
  ],
  languageSections: [
    {
      sectionKey: BASE_COUPON_PAGE_ANCHOR_KEY.couponGeneral.sectionKey,
      sectionTitle: 'general',
      sectionContent: [
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.couponGeneral.sectionContent.general_name_key,
          complexComponent: ({ language, formContext}) => {
            const couponSetContentSameAsCampaign = isCouponSetContentSameAsCampaign(formContext)
            return <ReactHookFormTextInput
              language={language}
              valueFieldName={"name"}
              translationFieldPrefix={`${fieldPrefix}translations`}
              withTranslation={true}
              focusKey='name'
              title='coupon_set_name'
              disabled={couponSetContentSameAsCampaign}
            />
          },
        },
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.couponGeneral.sectionContent.short_description_optional_key,
          complexComponent: ({ language, setActiveSection }) => (
            <ReactHookFormMarkDown
              content={{
                label: i18n.t('short_description_optional', { locale: language }),
                key: `${fieldPrefix}translations.${language}.shortDescription`,
                focus: () => {
                  setActiveSection('shortDescription');
                },
              }}
            />
          ),
        },
      ],
    },
    {
      sectionKey: BASE_COUPON_PAGE_ANCHOR_KEY.couponKeyVisual.sectionKey,
      sectionTitle: 'key_visual',
      sectionContent: [
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.couponKeyVisual.sectionContent.cover_photo_upload_key,
          complexComponent: ({
            language,
            setActiveSection,
            formContext,
          }) => {
            const couponSetContentSameAsCampaign = isCouponSetContentSameAsCampaign(formContext)
            return <PhotoUploaderWithSwitch
              language={language}
              withTranslation={true}
              title='cover_photo'
              tips='cover_photo_suggest_with_variable'
              wordTranslationVariable={{
                suggestHeight: 1800,
                minHeight: 600,
              }}
              tranlationFieldPrefix={`${fieldPrefix}translations`}
              photoFieldName='coverPhoto'
              activeSectionKey='coverPhoto'
              switchConfig={{
                fieldName: `${fieldPrefix}applyCoverPhotoToAllLanguage`,
                title: 'Apply to all language',
              }}
              uploaderAspect={900 / 600}
              minWidth={900}
              minHeight={600}
              focusChange={setActiveSection}
              disabled={couponSetContentSameAsCampaign}
            />
          },
        },
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.couponKeyVisual.sectionContent.other_photo_upload_key,
          complexComponent: ({
            language,
            setActiveSection,
            formContext,
          }) => {
            const couponSetContentSameAsCampaign = isCouponSetContentSameAsCampaign(formContext)
            return <PhotoUploaderWithSwitch
              language={language}
              withTranslation={true}
              title='other_photos'
              tips='cover_photo_suggest_with_variable'
              wordTranslationVariable={{
                suggestHeight: 1800,
                minHeight: 600,
              }}
              tranlationFieldPrefix={`${fieldPrefix}translations`}
              photoFieldName='otherPhoto'
              activeSectionKey='otherPhotos'
              switchConfig={{
                fieldName: `${fieldPrefix}applyDetailPhotosToAllLanguage`,
                title: 'Apply to all language',
              }}
              uploaderAspect={900 / 600}
              minWidth={900}
              minHeight={600}
              maxImageNum={4}
              focusChange={setActiveSection}
              disabled={couponSetContentSameAsCampaign}
            />
          },
        },
      ],
    },
    {
      sectionKey: BASE_COUPON_PAGE_ANCHOR_KEY.couponInstruction.sectionKey,
      sectionContent: [
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.couponInstruction.sectionContent.instruction_section_key,
          complexComponent: ({
            setActiveSection,
            language,
          }) => {
            return (
              <ReactHookFormMarkDownArea
                hasTranslation
                areaTitle="instruction"
                focusChange={setActiveSection}
                language={language}
                titleConfig={{
                  label: 'instruction_section_title_optional',
                  valueFieldName:
                    `${fieldPrefix}translations.{{language}}.instructionSectionTitle`,
                  disableFocusChange: false,
                  focusChangeKey: 'instructionTitle',

                }}
                contentConfig={{
                  label: 'instruction_section_content_optional',
                  valueFieldName:
                    `${fieldPrefix}translations.{{language}}.instructionSectionContent`,
                  disableFocusChange: false,
                  focusChangeKey: 'instructionContent',
                }}
              />
            );
          },
        },
      ],
    },
    {
      sectionKey: BASE_COUPON_PAGE_ANCHOR_KEY.couponDetail.sectionKey,
      sectionContent: [
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.couponDetail.sectionContent.detail_section_key,
          complexComponent: ({
            setActiveSection,
            language,
          }) => {
            return (
              <ReactHookFormMarkDownArea
                focusChange={setActiveSection}
                language={language}
                areaTitle="detail"
                titleConfig={{
                  label: 'detail_section_title_optional',
                  valueFieldName:
                    `${fieldPrefix}translations.{{language}}.detailSectionTitle`,
                  disableFocusChange: false,
                  focusChangeKey: 'detailsTitle',
                }}
                contentConfig={{
                  label: 'detail_section_content_optional',
                  valueFieldName:
                    `${fieldPrefix}translations.{{language}}.detailSectionContent`,
                  disableFocusChange: false,
                  focusChangeKey: 'detailsContent',
                }}
                hasTranslation
              />
            );
          },
        },
      ],
    },
    {
      hideInOtherLanguages: true,
      sectionKey: BASE_COUPON_PAGE_ANCHOR_KEY.validPeriod.sectionKey,
      sectionContent: [
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.validPeriod.sectionContent.valid_period_type_key,
          component: CouponDateTimeSelector,
          componentProps: {
            title: "COUPON VALID PERIOD",
            fieldNameFrefix: fieldPrefix
          },
        },
      ],
    },
    {
      hideInOtherLanguages: true,
      sectionKey: BASE_COUPON_PAGE_ANCHOR_KEY.applicableStores.sectionKey,
      sectionTitle: "COUPON APPLICABLE STORES",
      sectionContent: [
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.applicableStores.sectionContent.store_selector_key,
          complexComponent: () => (
            <>
              <StoreSelector 
                fieldNamePrefix={fieldPrefix}
                storesFieldName={"selectedStores"}
              />
              <div className="split-dashed-line create-section-label-bottom-space" />
            </>
          )
        },
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.applicableStores.sectionContent.coupon_required_select_store_key,
          component: ReactHookFormTitleWithRadio,
          componentProps: {
            title: "Require customers to select a store to use the coupon",
            valueFieldName: `${fieldPrefix}shouldBeUsedInStores`,
            defaultValue: false,
            options: [
              { label: 'No', value: false },
              { label: 'Yes', value: true },
            ]
          }
        },
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.applicableStores.sectionContent.time_to_select_store_key,
          complexComponent: ({
            formContext: { watch },
          }) => {
            const campaignType = watch("campaignType")
            return <ReactHookFormTitleWithRadio
              disabled={isCampaign && campaignType === CampaignType.earningRulesCampaign}
              title="When to select the store"
              valueFieldName={`${fieldPrefix}customerStoreSelectionBehavior`}
              defaultValue={!isCampaign || campaignType === CampaignType.couponsCampaign
                ? CustomerSelectionBehavior.SELECT_WHEN_ACQUIRE
                : CustomerSelectionBehavior.SELECT_IN_WALLET
              }
              options={!isCampaign || campaignType === CampaignType.couponsCampaign ? [
                { label: 'From campaign when acquire', value: CustomerSelectionBehavior.SELECT_WHEN_ACQUIRE },
                { label: 'From campaign when acquire or in My Wallet after acquiring', value: CustomerSelectionBehavior.NO_LIMIT },
              ]: [
                { label: 'In My Wallet after acquiring', value: CustomerSelectionBehavior.SELECT_IN_WALLET },
              ]}
            />
          },
        },
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.applicableStores.sectionContent.related_brand_optional_key,
          complexComponent: ({
            language,
          }) => {
            return (
              <>
                <div className="split-dashed-line create-section-label-bottom-space" />
                <ReactHookFormMulitSelectDropdown
                  title={'Select brand to be displayed on front end (optional)'}
                  description={"The front end currently displays only one brand at a time. No brand will be shown if none or multiple brands are selected."}
                  placeholder={"Search by brand ID, name"}
                  getSourceDataFromState={(state) => {
                    return state.brand.filteredBrandList.frontendDisplayBrands || []
                  }}
                  fieldPrefix={fieldPrefix}
                  valueFieldName={"displayOnFrontendBrands"}
                  customClass={'filter-title'}
                  loadMoreAction={'brand/getCurrentPageBrands'}
                  filterAction={'brand/getCurrentPageBrands'}
                  defaultFilter={{
                    filterKey: "frontendDisplayBrands",
                  }}
                  disabled={language !== LanguageConfig.english}
                />
              </>
            );
          },
        },
      ],
    },
    {
      hideInOtherLanguages: true,
      sectionKey: BASE_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionKey,
      sectionContent: [
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.couponProperties.sectionContent.coupon_properties_section_key,
          complexComponent: ({
            formContext: { watch },
          }) => {
            const couponStores = watch(`${fieldPrefix}selectedStores`);
            const couponSetMedium = getCouponSetMedium(couponStores)
            return <CouponSettingSection
              fieldNamePrefix={fieldPrefix}
              couponSetMedium={couponSetMedium}
            />
          },
        },
      ],
    },
    {
      hideInOtherLanguages: true,
      sectionKey: BASE_COUPON_PAGE_ANCHOR_KEY.generateCoupons.sectionKey,
      sectionTitle: "Import / Generate single coupons (Optional)",
      sectionContent: [
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.generateCoupons.sectionContent.generate_coupons_key,
          component: CampaignStreamlineGenerateCouponSection,
          componentProps: {
            couponSetFieldPrefix: fieldPrefix,
          }
        },
      ],
    },
    {
      hideInOtherLanguages: true,
      sectionKey: BASE_COUPON_PAGE_ANCHOR_KEY.additionalData.sectionKey,
      sectionContent: [
        {
          key: BASE_COUPON_PAGE_ANCHOR_KEY.additionalData.sectionContent.additional_data_key,
          component: ReactHookFormAdditionalDataSection,
          componentProps: {
            sectionKeyPrefix: BASE_COUPON_PAGE_ANCHOR_KEY.additionalData.sectionContent.additional_data_key,
            fieldPrefix: fieldPrefix,
          }
        }
      ]
    },
  ],
}); 

export const CREATE_CAMPAIGN_COUPON_PAGE_CONFIG = getCouponPageConfig({
  fieldPrefix: couponSetFieldPrefix,
  isCampaign: true,
});
